import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const DynamicModal = ({
  isOpen,
  toggle,
  acceptText = 'Close',
  children,
  size = 'lg',
  backdrop = true,
  dataId = 'dynamic-modal',
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleAccept = useCallback(() => {
    if (toggle) toggle();
  }, [toggle]);

  const handleContentLoaded = useCallback(() => {
    setIsLoading(false);
  }, []);

  const childrenWithLoadEvent = useMemo(() => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === 'iframe') {
        return React.cloneElement(child, {
          onLoad: handleContentLoaded,
          sandbox:
            'allow-same-origin allow-scripts allow-forms allow-downloads',
          referrerPolicy: 'strict-origin-when-cross-origin',
        });
      }
      return child;
    });
  }, [children, handleContentLoaded]);

  if (!isOpen) return null;

  return (
    <>
      {backdrop && (
        <div
          className="modal-backdrop show"
          onClick={toggle}
          style={{ zIndex: 1040 }}
        ></div>
      )}
      <div
        className="modal show d-block"
        tabIndex="-1"
        data-id={dataId}
        data-component="DynamicModal"
        style={{ zIndex: 1050 }}
      >
        <div className={`modal-dialog modal-${size}`}>
          <div className="modal-content">
            <div className="modal-body">
              {isLoading && (
                <div
                  className="text-center py-4"
                  role="status"
                  aria-live="polite"
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden"></span>
                  </div>
                  <p className="mt-2">Loading content...</p>
                </div>
              )}
              <div style={{ display: isLoading ? 'none' : 'block' }}>
                {childrenWithLoadEvent}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary w-100"
                onClick={handleAccept}
                type="button"
              >
                {acceptText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

DynamicModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func,
  acceptText: PropTypes.string,
  children: PropTypes.node,
  size: PropTypes.string,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  dataId: PropTypes.string,
};

export default DynamicModal;
